import {
  RBPayrollPaymentMethod,
  RBPayrollStatus,
} from "../types/rbPayrollOwner.types";

export const RB_PAY_INFO_TEXT = `RB Pay (All-In-One Payments)
RB Pay simplifies how you pay for shifts by handling both platform fees and worker payments in one seamless transaction.
When using RB Pay:
Relief Buddy processes the full shift payment for you.
Workers can be paid directly through the platform.
Where applicable, workers may choose to be paid as employees, with all required payroll deductions handled automatically.
Eligible independent contractors are paid accordingly, with proper invoicing and tax treatment.
This means no separate payroll, e-transfers, or manual calculations—everything is managed for you, with clear records and reporting available in your account.`;

export const RB_PAYROLL_CONTACT_PREFIX = "Questions? Contact us";
export const RB_PAYROLL_STATEMENT_ACCESS_NOTE =
  "Opens in new window. Use your RB Sign in email to access.";
export const RB_PAYROLL_ALLOWED_DOCUMENT_TYPES = [
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/jpg",
];
export const RB_PAYROLL_MAX_DOCUMENT_SIZE = 10 * 1024 * 1024;
export const RB_PAYROLL_DOCUMENT_HELPER_TEXT =
  "Accepted: Void cheque, or bank-issued account details (PDF or screenshot)";
export const RB_PAYROLL_AUTHORIZATION_TEXT =
  "I authorize Relief Buddy Inc. via RBPay to debit my bank account for amounts owed in connection with my use of the platform, including applicable fees and adjustments. I confirm that the banking information provided is accurate and belongs to me. I understand that these debits will be processed in accordance with applicable payment rules and may vary based on my account activity. I acknowledge that I may revoke this authorization at any time with reasonable notice, subject to any outstanding obligations.";

export const RB_PAYROLL_AUTHORIZED_SUMMARY =
  "You have authorized Relief Buddy Inc. via RB Pay to debit your bank account for amounts owed in connection with your use of the platform.";

// Step checklist (Owner has 2 steps).
export const RB_PAYROLL_STEPS = {
  banking: { label: "Banking Details", caption: "Institution & account" },
  authorization: { label: "Authorization", caption: "Review & authorize" },
};

// About RB Pay banner (purple) — mirrors the Worker banner with owner bullets.
export const RB_PAYROLL_ABOUT_TITLE = "RB Pay (All-In-One Payments)";
export const RB_PAYROLL_ABOUT_INTRO =
  "RB Pay simplifies how you pay for shifts by handling both platform fees and worker payments in one seamless transaction.\n\nWhen using RB Pay:";
export const RB_PAYROLL_ABOUT_BULLETS = [
  "Relief Buddy processes the full shift payment for you.",
  "Workers can be paid directly through the platform.",
  "Where applicable, workers may choose to be paid as employees, with all required payroll deductions handled automatically.",
  "Eligible independent contractors are paid accordingly, with proper invoicing and tax treatment.",
  "This means no separate payroll, e-transfers, or manual calculations — everything is managed for you, with clear records and reporting available in your account.",
];

// Submission success / error messaging (mirrors the Worker RBAlert set).
export const RB_PAYROLL_FIRST_SUBMIT_TITLE = "RB Pay Setup Submitted";
export const RB_PAYROLL_FIRST_SUBMIT_MESSAGE =
  "Your RB Pay setup has been submitted and is now pending review.\n\n" +
  "Our team will review your banking details before this location is activated for RB Pay. We'll notify you once it's approved.";
export const RB_PAYROLL_EDIT_SUBMIT_TITLE = "Changes Submitted";
export const RB_PAYROLL_EDIT_SUBMIT_MESSAGE =
  "Your updated RB Pay banking details have been submitted.\n\n" +
  "Because these changes affect how this location is debited, they'll be reviewed again before taking effect — your RB Pay status may return to pending until re-approved. We'll notify you once the review is complete.";
export const RB_PAYROLL_COPY_SUBMIT_TITLE = "RB Pay Setup Copied";
export const RB_PAYROLL_COPY_SUBMIT_MESSAGE =
  "RB Pay banking details have been successfully copied from the selected location and are now set up for this location.\n\n" +
  "We'll notify you if any further review is needed before this location is activated for RB Pay.";
export const RB_PAYROLL_SUBMIT_ERROR_TITLE = "Submission Failed";
export const RB_PAYROLL_SUBMIT_ERROR_MESSAGE =
  "We couldn't submit your RB Pay setup right now. Please check your connection and try again.\n\n" +
  "If the problem continues, please contact support.";
export const RB_PAYROLL_NO_CHANGES_TOOLTIP = "No changes to submit.";

export const RB_PAYROLL_PAYMENT_OPTION_CONFIG = {
  id: "rb_payroll",
  type: RBPayrollPaymentMethod.RB_PAYROLL,
  title: "RB Pay (All-In-One Payments)",
  description:
    "Simplify payments by covering both platform fees and worker pay in one transaction.\nNo separate payroll, e-transfer, or credit card processing required.",
  setupRequired: true,
  setupStatus: RBPayrollStatus.NOT_STARTED,
  approved: false,
};

export const RB_PAYROLL_STATUS_COPY = {
  [RBPayrollStatus.NOT_STARTED]: {
    title: "RB Pay has not been configured yet.",
    description: "Complete RB Pay Setup to submit your banking details for review.",
    badgeLabel: "Not Started",
  },
  [RBPayrollStatus.PENDING_REVIEW]: {
    title: "RB Pay Pending Review",
    description:
      "Your RB Pay setup has been submitted and is currently under review. You will be notified once approval has been completed.",
    badgeLabel: "Pending Review",
  },
  [RBPayrollStatus.ACTIVE]: {
    title: "RB Pay Active",
    description: "RB Pay is active for this location.",
    badgeLabel: "Active",
  },
  [RBPayrollStatus.ON_HOLD]: {
    title: "RB Pay On Hold",
    description:
      "RB Pay for this location is currently on hold. Please review your details or contact support to have it reactivated.",
    badgeLabel: "On Hold",
  },
  [RBPayrollStatus.REJECTED]: {
    title: "RB Pay Setup Requires Attention",
    description: "Your RB Pay setup was rejected and needs to be updated before it can be approved.",
    badgeLabel: "Requires Attention",
  },
};

// State-aware checklist notice (mirrors the worker RB_PAY_SETUP_NOTES): the copy
// reflects where the owner is in the flow rather than just "all steps filled".
export const RB_PAYROLL_SETUP_NOTES = {
  incomplete: "Your setup is not complete until you click Save Setup at the bottom of this page.",
  editing:
    "You're editing your RB Pay details — click Save Setup to resubmit. Changes go back for review and may need re-approval before taking effect.",
  submitted: "Your RB Pay setup has been submitted and is pending review.",
  onHold: "Your RB Pay is on hold. Review your details or contact support to have this location reactivated.",
  rejected: "Your RB Pay setup needs attention — update your details and click Save Setup to resubmit.",
  complete:
    "Your RB Pay setup is complete. You can edit your details anytime — updates are resubmitted for review and may need re-approval before taking effect.",
};

export const RB_PAYROLL_SETUP_SELECTION_COPY = {
  title: "How would you like to configure RB Pay?",
  copyTitle: "Use Existing RB Pay Setup",
  copyDescription:
    "Copy RB Pay banking details from another location on this account and apply them to this location in one step.",
  newTitle: "Create New RB Pay Setup",
  newDescription:
    "Start fresh and enter new RB Pay banking details for this location.",
  copySelectionNote:
    "The selected location's RB Pay banking details will be copied to this location and submitted right away. You can review or update them later from RB Pay Setup.",
  newSelectionNote:
    "You can continue with a brand-new RB Pay setup for this location.",
  emptySourcesNote:
    "No eligible RB Pay setups were found on your other locations yet. You can continue by creating a new RB Pay setup.",
  sourceRequiredError: "Select an existing location before continuing.",
};
